/* eslint-disable max-lines */
import { useLingui } from '@lingui/react';
import {
  DEFAULT_GAS_LIMIT,
  SupportedChainId,
  TxStatus,
} from '@orionprotocol/sdk';
import { getKnownWalletError } from '@orionprotocol/wallet-api';
import BigNumber from 'bignumber.js';
import { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { z } from 'zod';
import {
  txOnChainCallbacks,
  txOnErrorCallbacks,
  txOnFailCallbacks,
  txOnFinalCallbacks,
  txOnSentCallbacks,
  txOnSuccessCallbacks,
} from '@orionprotocol/common/src/transactions/transactionCallbacks';
import { useToast } from '@orionprotocol/ui';
import { sentryLogger } from '@orionprotocol/common';
// import { OrderStatus } from '@orionprotocol/trading-terminal/src/types';
// import { useHistoryOrders } from '@orionprotocol/trading-terminal/src/hooks/useHistoryOrders';
import { isResendableTx } from '@orionprotocol/common/src/transactions';

import {
  useAppChainId,
  useGetAllTransactions,
  useWalletAddress,
} from '../../shared/api';
import {
  getGasPrice,
  getTransactionInSigningId,
} from '../../shared/redux/selectors';
import { useAppNetwork, useProviderOrSigner } from '../../shared/hooks';
import { getDisabledEstimateGas } from '../../shared/redux/selectors/debug';
import { useLumia, TransactionsProcessingContext } from '../../shared/context';
import { useProcessContractError } from '../../shared/contracts';
import { transactionsSlice } from '../../shared/redux/slices';
import { Contracts } from '../../shared/types';

// const finalStatuses: OrderStatus[] = [
//   'CANCELED',
//   'DIRECT_SWAP_FAILED',
//   'FAILED',
//   'REJECTED',
//   'SETTLED',
// ];

const ethersErrorSchema = z.object({
  code: z.string(),
  method: z.string(),
  reason: z.string(),
  error: z.object({
    body: z.string(),
    code: z.string(),
    reason: z.string(),
    requestBody: z.string(),
    requestMethod: z.string(),
    url: z.string(),
    error: z.object({
      code: z.number(),
      data: z.string(),
    }),
  }),
  transaction: z.object({
    data: z.string(),
    to: z.string(),
    from: z.string(),
  }),
});

const blockchainNodeErrorResponse = z.object({
  id: z.number(),
  jsonrpc: z.literal('2.0'),
  error: z.object({
    code: z.number(),
    data: z.string(),
    message: z.string(),
  }),
});

// HACK: for POLYGON and FANTOM networks increase gasLimit by 25%
// refer to https://orionprotocol.atlassian.net/browse/OP-3280
function getSwapThroughOrionPoolGasLimit(
  chainId: SupportedChainId,
  gasLimit: BigNumber,
): string {
  const finalGasLimit = [
    SupportedChainId.POLYGON,
    SupportedChainId.FANTOM_OPERA,
  ].includes(chainId)
    ? gasLimit.multipliedBy(1.25)
    : gasLimit;

  return finalGasLimit.toFixed(0);
}

// Need to detect tx in signing queued in current session or not
let inSigningTxId: string | null = null;

const TX_LIMIT = 15;

export function TransactionsProcessingProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const { i18n } = useLingui();
  const dispatch = useDispatch();
  const appChainId = useAppChainId();
  const walletAddress = useWalletAddress();
  const transactions = useGetAllTransactions();
  const userChainTransactions = useMemo(
    () =>
      [...transactions]
        .reverse()
        .filter((tx) => tx.from === walletAddress && tx.chainId === appChainId),
    [appChainId, transactions, walletAddress],
  );

  const queuedTransactions = useMemo(
    () => userChainTransactions.filter((tx) => tx.status === TxStatus.QUEUED),
    [userChainTransactions],
  );
  const pendingTransactions = useMemo(
    () => userChainTransactions.filter((tx) => tx.status === TxStatus.PENDING),
    [userChainTransactions],
  );

  const txInSigning = userChainTransactions.find(
    ({ status }) => status === TxStatus.SIGNING,
  );
  const transactionInSigningId = useSelector(getTransactionInSigningId);
  const gasPriceGwei = useSelector(getGasPrice);
  const { explorer } = useAppNetwork();
  const estimateGasDisabled = useSelector(getDisabledEstimateGas);
  const { signer } = useProviderOrSigner();

  const { availableUnits } = useLumia();
  const { showToast } = useToast();

  const processContractError = useProcessContractError();

  const updateTxStatus = useCallback(
    (id: string, status: TxStatus) => {
      dispatch(
        transactionsSlice.actions.updateTransaction({
          id,
          params: { status },
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (inSigningTxId === null) {
      if (txInSigning) {
        // We can't know what happened with this transaction
        // Possibly is sent. Possibly not.
        updateTxStatus(txInSigning.id, TxStatus.UNKNOWN);
      }
      // If transactionInSigningId saved from previous session (before page reload)
      // We clear it to unblock queue
      if (transactionInSigningId !== null) {
        dispatch(transactionsSlice.actions.setTransactionInSigningId(null));
        queuedTransactions.forEach((tx) =>
          updateTxStatus(tx.id, TxStatus.CANCELLED),
        ); // And all queued txs
      }
    }
  }, [
    dispatch,
    queuedTransactions,
    transactionInSigningId,
    txInSigning,
    updateTxStatus,
  ]);

  // Cleaner
  useEffect(() => {
    if (
      walletAddress !== undefined &&
      userChainTransactions.length > TX_LIMIT
    ) {
      dispatch(
        transactionsSlice.actions.removeOldest({
          user: walletAddress,
          chain: appChainId,
        }),
      );
    }
  }, [appChainId, dispatch, userChainTransactions.length, walletAddress]);

  // Handle the transactions queue
  useEffect(() => {
    pendingTransactions.forEach((tx) => {
      const provider = availableUnits[tx.chainId]?.provider;

      if (tx.hash === undefined || provider === undefined) return;

      provider
        .waitForTransaction(tx.hash, tx.confirmations)
        .then((txReceipt) => {
          updateTxStatus(
            tx.id,
            txReceipt?.status === 1 ? TxStatus.SETTLED : TxStatus.FAILED,
          );
          if (txReceipt?.status === 1) {
            txOnSuccessCallbacks.get(tx.id)?.(txReceipt);
          } else {
            txOnFailCallbacks.get(tx.id)?.();
          }
        })
        .catch((e) => {
          updateTxStatus(tx.id, TxStatus.FAILED);
          txOnFailCallbacks.get(tx.id)?.();
        })
        .finally(() => txOnChainCallbacks.get(tx.id)?.());
    });
  }, [availableUnits, pendingTransactions, updateTxStatus]);

  const handleError = useCallback(
    (
      e: Error,
      id: string,
      status: TxStatus,
      contract: Contracts | undefined,
    ) => {
      const knownWalletError = getKnownWalletError(e);
      if (knownWalletError) {
        showToast({
          text: knownWalletError.message,
          iconType: 'error',
        });
        return;
      }

      sentryLogger.error(e);
      updateTxStatus(id, status);

      try {
        const errorData = ethersErrorSchema.parse(e);
         
        const errorBody = JSON.parse(errorData.error.body);

        const { error } = blockchainNodeErrorResponse.parse(errorBody);

        const regex = /execution reverted:?\s*(.*)/gm;
        const str = error.message;
        const match = regex.exec(str);

        if (match) {
          const revertMessage = match[1] || errorData.reason;

          // If we have error with Fee tokens slippage "PoolFunctionality: OOS"
          // we'll show notification instead
          if (revertMessage.toUpperCase().includes('OOS')) {
            showToast({
              iconType: 'error',
              text: i18n._(
                'This pair has a token with an internal tax/commission. Try increasing the max price deviation (under "Settings") to create an order.',
              ),
            });
          } else {
            showToast({
              iconType: 'error',
              text: i18n._('Contract {data} revert: {revertMessage}', {
                data: errorData.transaction.to,
                revertMessage,
              }),
            });
          }
        } else {
          let errorMessage = i18n._(
            'Something went wrong with transaction processing. Please reload the page and try again.',
          );

          if (status === TxStatus.SIGN_FAILED) {
            errorMessage = i18n._(
              'Transaction sign failed. Please reload the page and try again.',
            );
          }
          if (status === TxStatus.ESTIMATE_GAS_FAILED) {
            errorMessage = i18n._(
              'Gas estimation failed. Please reload the page and try again.',
            );
          }

          console.error(errorData);
          showToast({
            iconType: 'error',
            text: errorMessage,
          });
        }
      } catch (err) {
        processContractError(e, contract);
      }
      txOnErrorCallbacks.get(id)?.(e);
    },
    [i18n, processContractError, showToast, updateTxStatus],
  );

  useEffect(() => {
    if (
      queuedTransactions.length > 0 &&
      signer &&
      transactionInSigningId === null &&
      gasPriceGwei !== undefined
    ) {
      // Process oldest tx in queue
      const { id, value, chainId, from, to, data, gasLimit, contract } =
        queuedTransactions[0];

      const provider = availableUnits[chainId]?.provider;
      if (provider === undefined) return;

      inSigningTxId = id;
      dispatch(transactionsSlice.actions.setTransactionInSigningId(id));
      const tx = {
        value: value ?? '0',
        from,
        to,
        data,
        // gasPrice: ethers.parseUnits(gasPriceGwei, 'gwei'),
        chainId: parseInt(chainId, 10),
      };

      void (async (): Promise<void> => {
        let orionGasLimit: BigNumber | undefined;
        if (gasLimit !== undefined) {
          orionGasLimit = BigNumber(gasLimit);
        }
        let txGasLimit = orionGasLimit;

        if (estimateGasDisabled) orionGasLimit = BigNumber(DEFAULT_GAS_LIMIT);
        if (!estimateGasDisabled) {
          try {
            updateTxStatus(id, TxStatus.GAS_ESTIMATING);
            const estimatedGasLimit = BigNumber(
              (await provider.estimateGas(tx)).toString(),
            );
            const safeGasLimit =
              orionGasLimit !== undefined
                ? BigNumber.max(estimatedGasLimit, orionGasLimit)
                : estimatedGasLimit;
            txGasLimit = BigNumber(
              getSwapThroughOrionPoolGasLimit(chainId, safeGasLimit),
            );
          } catch (e) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            handleError(e as Error, id, TxStatus.ESTIMATE_GAS_FAILED, contract);
            inSigningTxId = null;
            dispatch(transactionsSlice.actions.setTransactionInSigningId(null));
            return;
          }
        }

        try {
          updateTxStatus(id, TxStatus.SIGNING);
          const txResponse = await signer.sendTransaction({
            ...tx,
            // increase gas limit to prevent tx fail
            gasLimit:
              txGasLimit !== undefined
                ? txGasLimit.multipliedBy(1.5).toFixed(0, BigNumber.ROUND_DOWN)
                : undefined,
          });
          dispatch(
            transactionsSlice.actions.updateTransaction({
              id,
              params: {
                hash: txResponse.hash,
                status: TxStatus.PENDING,
              },
            }),
          );
          txOnSentCallbacks.get(id)?.(txResponse);
          // dispatch(swapSlice.actions.setSuccessTransaction(true));
          showToast({
            text: i18n._(
              'Your transaction is taking place. You can continue using Lumia',
            ),
            iconType: 'success',
            link: `${explorer}tx/${txResponse.hash}`,
            linkText: i18n._('Explore'),
          });
        } catch (e) {
          updateTxStatus(id, TxStatus.SIGN_FAILED);
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          handleError(e as Error, id, TxStatus.SIGN_FAILED, contract);
          txOnFailCallbacks.get(id)?.(TxStatus.SIGN_FAILED);
        } finally {
          inSigningTxId = null;
          dispatch(transactionsSlice.actions.setTransactionInSigningId(null));
          txOnFinalCallbacks.get(id)?.();
        }
      })();
    }
  }, [
    availableUnits,
    dispatch,
    estimateGasDisabled,
    explorer,
    gasPriceGwei,
    handleError,
    i18n,
    queuedTransactions,
    showToast,
    signer,
    transactionInSigningId,
    updateTxStatus,
  ]);

  // TODO: This functionality (balance lock tracking) should be moved out of this provider
  // const historyOrdersFromLocalStorage = useHistoryOrders(true);
  //
  // const pendingSwapOrders = useMemo(
  //   () =>
  //     historyOrdersFromLocalStorage.filter(
  //       (order) => !finalStatuses.includes(order.status), // Order is not finalized
  //     ),
  //   [historyOrdersFromLocalStorage],
  // );

  // useEffect(() => {
  //   dispatch(
  //     userSlice.actions.lockPendingBalance({
  //       orders: pendingSwapOrders,
  //       chainId: appChainId,
  //     }),
  //   );
  // }, [appChainId, dispatch, pendingSwapOrders]);

  const resendTx = useCallback(
    (id: string) => {
      const transaction = transactions.find((tx) => tx.id === id);
      if (transaction === undefined)
        throw new Error(
          i18n._('Resend failed: Transaction ID {id} not found.', { id }),
        );

      // Check that transaction not in final state
      // final state can not be resent
      if (!isResendableTx(transaction))
        throw new Error(
          i18n._('Transaction cannot be re-sent. Status: {status}', {
            status: transaction.status ?? i18n._('[UNDEFINED]'),
          }),
        );

      dispatch(
        transactionsSlice.actions.updateTransaction({
          id: transaction.id,
          params: {
            status: TxStatus.QUEUED,
          },
        }),
      );
    },
    [dispatch, i18n, transactions],
  );

  const memoedValue = useMemo(
    () => ({
      resendTx,
    }),
    [resendTx],
  );

  return (
    <TransactionsProcessingContext.Provider value={memoedValue}>
      {children}
    </TransactionsProcessingContext.Provider>
  );
}
